<template>
  <v-card :elevation="$vuetify.breakpoint.xsOnly ? 0 : ''" class="mb-4">
    <v-card-text>
      <div class="d-flex align-center flex-wrap">
        <v-icon class="mr-2 no-gradient">mdi-map-marker-outline</v-icon>
        <span class="subtitle-1 font-weight-medium">{{ site.name || $t('quotations.calc.site') }}</span>
        <span v-if="site.address && !editing" class="body-2 grey--text ml-2">{{ site.address }}</span>
        <v-spacer />
        <template v-if="!readonly">
          <v-btn v-if="!editing" small text color="primary" :disabled="saving" @click="editing = true">
            <v-icon small left class="no-gradient">mdi-pencil</v-icon>
            {{ $t('quotations.calc.edit_site') }}
          </v-btn>
          <v-btn v-else small text color="primary" :disabled="saving" @click="editing = false">
            <v-icon small left class="no-gradient">mdi-check</v-icon>
            {{ $t('quotations.calc.done_editing') }}
          </v-btn>
          <v-btn icon small :disabled="saving" :title="$t('quotations.calc.delete_site')" @click="$emit('remove')">
            <v-icon small class="no-gradient" color="error">delete</v-icon>
          </v-btn>
        </template>
      </div>

      <div v-if="!editing" class="site-details body-2">
        <div v-if="site.additionalInfo" class="site-info">{{ site.additionalInfo }}</div>
        <div v-if="(site.soilTypes || []).length > 0">
          <span class="grey--text">{{ $t('quotations.basics.soil_types') }}:</span> {{ site.soilTypes.join(', ') }}
        </div>
      </div>

      <v-row v-else dense class="mt-2">
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="site.name"
            :label="$t('quotations.calc.site_name')"
            :disabled="saving"
            :rules="[$rules.required, uniqueRule]"
            autofocus
            outlined
            dense
            hide-details="auto"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field v-model="site.address" :label="$t('quotations.calc.address')" :disabled="saving" outlined dense hide-details>
            <!-- Sijainti kartalta kuten työmaan asetuksissa; heti näkyvissä, koska sijainnin voi asettaa ilman osoitettakin -->
            <template v-slot:append-outer>
              <v-btn small text :disabled="saving" style="margin-top: -5.5px" @click="openMapDialog">
                <v-icon :left="$vuetify.breakpoint.mdAndUp" class="no-gradient">mdi-crosshairs-gps</v-icon>
                <span v-if="$vuetify.breakpoint.mdAndUp">{{ site.geocoding.address ? $t('sites.show_location') : $t('sites.set_location') }}</span>
              </v-btn>
            </template>
          </v-text-field>
        </v-col>
        <v-col v-if="site.geocoding.address" cols="12" sm="6" offset-sm="6">
          <v-text-field :value="site.geocoding.address" :label="$t('sites.address_by_coordinates')" disabled outlined dense hide-details />
        </v-col>
        <v-col cols="12">
          <v-textarea v-model="site.additionalInfo" :label="$t('quotations.calc.info')" :disabled="saving" outlined dense auto-grow rows="1" hide-details />
        </v-col>
        <v-col cols="12">
          <v-combobox
            v-model="site.soilTypes"
            :items="soilSuggestions"
            :label="$t('quotations.basics.soil_types')"
            :hint="$t('quotations.basics.soil_types_hint')"
            :disabled="saving"
            multiple
            small-chips
            deletable-chips
            outlined
            dense
            hide-details="auto"
          />
        </v-col>
      </v-row>

      <quotation-row-list class="mt-4" :rows="site.rows" :work-methods="workMethods" :items="items" :readonly="readonly" :saving="saving" />
    </v-card-text>
    <map-dialog ref="mapDialog" @change="setLocation" />
  </v-card>
</template>

<script>
import geocodingApi from '@/api/geocoding';
import MapDialog from '@/components/MapDialog';
import QuotationRowList from './QuotationRowList';

export default {
  name: 'QuotationSiteCard',
  components: { MapDialog, QuotationRowList },
  props: {
    site: { type: Object, required: true },
    // Muiden työmaiden nimet uniikkiustarkistukseen
    siblingNames: { type: Array, default: () => [] },
    workMethods: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      // Uusi, nimetön työmaa avataan suoraan muokattavaksi
      editing: !this.site.name && !this.readonly,
      soilSuggestions: ['savi', 'siltti', 'hiesu', 'hiekka', 'sora', 'kivet', 'moreeni', 'kallio', 'louhe', 'täyttömaa'],
    };
  },
  methods: {
    uniqueRule(v) {
      const name = (v || '').trim().toLowerCase();
      return !this.siblingNames.some(n => (n || '').trim().toLowerCase() === name) || this.$t('quotations.calc.site_name_duplicate');
    },
    openMapDialog() {
      this.$refs.mapDialog.open(this.site);
    },
    // Kuten SiteView.setLocation: piste kartalta ja sen osoite koordinaateista
    async setLocation({ lat, lng }) {
      this.site.geocoding = { lat, lng, address: '' };
      const found = await geocodingApi.getByLatLng(lat, lng);
      this.site.geocoding.address = found?.[0]?.address || '';
    },
  },
};
</script>

<style scoped>
.site-details {
  padding-left: 32px;
}
.site-info {
  white-space: pre-line;
}
</style>
